/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Drawer } from 'antd';
import { useEffect, useState } from 'react';

import { ConfigRevision, NodeConfig, NodeGroup } from '@/client';
import { useMessage } from '@/hooks/message';
import { useAssignConfigToGroupMutation } from '@/hooks/mutation/useAssignConfigToGroupMutation';
import { usePaginatedConfigRevisionsQuery } from '@/hooks/query/usePaginatedConfigRevisionsQuery';
import { usePaginatedNodeConfigsQuery } from '@/hooks/query/usePaginatedNodeConfigsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { SelectConfigStep } from '@/pages/FleetManagement/NewFleetWizardSteps/SelectConfigStep';
import { SelectRevisionStep } from '@/pages/FleetManagement/NewFleetWizardSteps/SelectRevisionStep';
import { Button } from '@/primitives/Button';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { UpdateFleetConfigFleetDetails } from './UpdateFleetConfigFleetDetails';
import { UpdateFleetConfigReviewStep } from './UpdateFleetConfigReviewStep';
import './UpdateFleetConfigDrawer.css';

type WizardStep = 'selectConfig' | 'selectRevision' | 'review';

export type UpdateFleetConfigDrawerProps = {
  open: boolean;
  onClose: () => void;
  nodeGroup: NodeGroup;
};

export function UpdateFleetConfigDrawer({ open, onClose, nodeGroup }: UpdateFleetConfigDrawerProps) {
  const [message] = useMessage();
  const { t } = useMimicTranslation('fleets');
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const [currentStep, setCurrentStep] = useState<WizardStep>('selectConfig');
  const [selectedConfig, setSelectedConfig] = useState<NodeConfig | undefined>();
  const [selectedRevision, setSelectedRevision] = useState<ConfigRevision | undefined>();

  const configsQuery = usePaginatedNodeConfigsQuery({
    params: {
      filters: { os: nodeGroup.operatingSystem },
    },
    type: 'inMemory',
  });

  const revisionsQuery = usePaginatedConfigRevisionsQuery({
    params: {
      configId: selectedConfig?.id || '',
    },
    type: 'inMemory',
  });

  useEffect(() => {
    setSelectedRevision(undefined);
  }, [selectedConfig?.id]);

  const { mutate: assignConfigToGroup, isPending } = useAssignConfigToGroupMutation({
    onSuccess: () => {
      message.success(t('updateConfigDrawer.feedback.success'));
      handleClose();
    },
    onError: () => {
      message.error(t('updateConfigDrawer.feedback.error'));
    },
  });

  const handleClose = () => {
    setCurrentStep('selectConfig');
    setSelectedConfig(undefined);
    setSelectedRevision(undefined);
    onClose();
  };

  const handleNext = () => {
    if (currentStep === 'selectConfig') {
      setCurrentStep('selectRevision');
    } else if (currentStep === 'selectRevision') {
      setCurrentStep('review');
    }
  };

  const handlePrevious = () => {
    if (currentStep === 'selectRevision') {
      setCurrentStep('selectConfig');
    } else if (currentStep === 'review') {
      setCurrentStep('selectRevision');
    }
  };

  const handleSubmit = () => {
    if (!selectedConfig || !selectedRevision) return;

    assignConfigToGroup({
      nodeGroupId: nodeGroup.id,
      data: {
        configId: selectedConfig.id,
        revisionNumber: selectedRevision.revisionNumber,
      },
    });
  };

  const getNodeConfigPath = (config: NodeConfig) => `/tenants/${tenantId}/node-configs/${config.id}`;
  const getNodeConfigRevisionPath = (revision: ConfigRevision) =>
    `/tenants/${tenantId}/node-configs/${revision.nodeConfigId}/revisions/${revision.revisionNumber}`;

  const canProceed: Record<WizardStep, boolean> = {
    selectConfig: !!selectedConfig,
    selectRevision: !!selectedRevision,
    review: true,
  };

  const stepTitles: Record<WizardStep, string> = {
    selectConfig: t('updateConfigDrawer.steps.selectConfig'),
    selectRevision: t('updateConfigDrawer.steps.selectRevision'),
    review: t('updateConfigDrawer.steps.review'),
  };

  const footer = (
    <div className="update-fleet-config-drawer-footer">
      <div className="left-buttons">
        {currentStep !== 'selectConfig' && (
          <Button dataTestId="previous-step" type="default" onClick={handlePrevious} disabled={isPending}>
            <ArrowLeftOutlined />
            {t('wizard.previous')}
          </Button>
        )}
      </div>
      <div className="right-buttons">
        <Button dataTestId="cancel-update-config" type="link" onClick={handleClose} disabled={isPending}>
          {t('cancel')}
        </Button>
        {currentStep !== 'review' && (
          <Button dataTestId="next-step" type="primary" onClick={handleNext} disabled={!canProceed[currentStep]}>
            {t('wizard.next')}
            <ArrowRightOutlined />
          </Button>
        )}
        {currentStep === 'review' && (
          <Button
            dataTestId="update-fleet-config"
            type="primary"
            onClick={handleSubmit}
            disabled={isPending}
            loading={isPending}
          >
            {t('updateConfigDrawer.updateConfig')}
            <CheckOutlined />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Drawer
      rootClassName="update-fleet-config-drawer"
      placement="top"
      title={stepTitles[currentStep]}
      open={open}
      height="100vh"
      onClose={handleClose}
      closeIcon={null}
      extra={<CloseButton onClick={handleClose} />}
      footer={footer}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
        <UpdateFleetConfigFleetDetails nodeGroup={nodeGroup} />

        {currentStep === 'selectConfig' && (
          <SelectConfigStep
            configsQuery={configsQuery}
            selectedConfig={selectedConfig}
            onConfigSelect={setSelectedConfig}
            getNodeConfigPath={getNodeConfigPath}
            getNodeConfigRevisionPath={getNodeConfigRevisionPath}
          />
        )}

        {currentStep === 'selectRevision' && selectedConfig && (
          <SelectRevisionStep
            revisionsQuery={revisionsQuery}
            selectedRevision={selectedRevision}
            onRevisionSelect={setSelectedRevision}
            getConfigRevisionPath={getNodeConfigRevisionPath}
          />
        )}

        {currentStep === 'review' && selectedConfig && selectedRevision && (
          <UpdateFleetConfigReviewStep
            newConfig={selectedConfig}
            newRevision={selectedRevision}
            onChangeConfig={() => setCurrentStep('selectConfig')}
            onChangeRevision={() => setCurrentStep('selectRevision')}
          />
        )}
      </div>
    </Drawer>
  );
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <Button dataTestId="close-update-config-drawer" type="text" onClick={onClick}>
      <CloseOutlined />
    </Button>
  );
}
